import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Users, Baby, Clock, MapPin, User as UserIcon } from '@phosphor-icons/react';
import { apiOrigin } from '@/utils/apiBase';

const API = apiOrigin();
const POLL_INTERVAL = 30000;       // refresh kiosk data every 30s
const WELCOME_POLL = 5000;         // poll for new arrivals every 5s
const PHOTO_INTERVAL = 4500;       // rotate hero photo every 4.5s

export default function KioskPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const layout = params.get('layout') || 'split'; // split | photos | timeline
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [photoIdx, setPhotoIdx] = useState(0);
  const [now, setNow] = useState(new Date());
  const heroRef = useRef(null);

  // Spotlight state — when set, overlays a 5-second welcome card
  const [spotlight, setSpotlight] = useState(null); // { names: [...], key: number, source?: string }
  const [showPicker, setShowPicker] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const spotlightTimer = useRef(null);
  // Tracks the last welcome we've already shown so we don't replay it.
  const lastWelcomeIdRef = useRef('');
  // FIFO queue of pending welcomes (so multiple guests arriving quickly get serialised).
  const welcomeQueueRef = useRef([]);

  const triggerSpotlight = (names, source = 'manual') => {
    const clean = (names || []).filter(Boolean);
    if (clean.length === 0) return;
    if (spotlightTimer.current) clearTimeout(spotlightTimer.current);
    setSpotlight({ names: clean, key: Date.now(), source });
    setShowPicker(false);
    // Auto-dismiss after 5 s, then show the next queued welcome (if any)
    spotlightTimer.current = setTimeout(() => {
      setSpotlight(null);
      // Drain queue
      if (welcomeQueueRef.current.length > 0) {
        const next = welcomeQueueRef.current.shift();
        setTimeout(() => triggerSpotlight(next.names, next.source), 600);
      }
    }, 5000);
  };

  // Poll the welcome queue every WELCOME_POLL ms for new RSVP submits / QR check-ins.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/events/public/${slug}/kiosk/welcomes`, {
          params: lastWelcomeIdRef.current ? { since: lastWelcomeIdRef.current } : {},
        });
        if (cancelled) return;
        const items = r.data?.welcomes || [];
        if (items.length === 0) return;
        for (const w of items) {
          // If nothing is showing, fire immediately; else enqueue
          if (welcomeQueueRef.current.length === 0 && !spotlight) {
            triggerSpotlight(w.names, w.source);
          } else {
            welcomeQueueRef.current.push({ names: w.names, source: w.source });
          }
          lastWelcomeIdRef.current = w.welcome_id;
        }
      } catch { /* ignore polling errors */ }
    };
    poll(); // immediate first run
    const t = setInterval(poll, WELCOME_POLL);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => () => {
    if (spotlightTimer.current) clearTimeout(spotlightTimer.current);
  }, []);

  // Force dark theme on this page (it's meant for TV display) — restore on unmount
  useEffect(() => {
    const html = document.documentElement;
    const prevClasses = html.className;
    // Strip any theme-* class, force theme-dark
    const cleaned = prevClasses.replace(/\btheme-\S+\b/g, '').trim();
    html.className = `${cleaned} theme-dark`.trim();
    document.body.style.background = '#000';
    return () => {
      html.className = prevClasses;
      document.body.style.background = '';
    };
  }, []);

  const fetchData = async () => {
    try {
      const r = await axios.get(`${API}/api/events/public/${slug}/kiosk`);
      setData(r.data);
      setErr('');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not load');
    }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Rotate photos
  useEffect(() => {
    if (!data || data.photos.length === 0) return;
    const t = setInterval(() => setPhotoIdx(i => (i + 1) % data.photos.length), PHOTO_INTERVAL);
    return () => clearInterval(t);
  }, [data]);

  // Clock tick
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  if (err) return (
    <Full><p className="text-red-400 text-lg">{err}</p></Full>
  );
  if (!data) return (
    <Full><div className="w-10 h-10 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></Full>
  );

  const themeColor = data.event.theme_color || '#FF6B35';
  const heroPhoto = data.photos[photoIdx];
  const photoUrl = heroPhoto ? `${API}${heroPhoto.url}` : null;
  const clockStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

  return (
    <div className="min-h-screen overflow-hidden relative kiosk-tv-display"
      style={{
        background: `radial-gradient(circle at 20% 0%, ${themeColor}18 0%, transparent 50%), radial-gradient(circle at 80% 100%, ${themeColor}10 0%, transparent 50%), #050505`,
        color: '#fff',
        colorScheme: 'dark',
      }}>
      {/* Subtle accent border */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ boxShadow: `inset 0 0 120px ${themeColor}25` }} />

      <div className="relative h-screen flex flex-col p-8 gap-6">
        {/* HEADER */}
        <header className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="text-6xl">{data.event.theme_emoji}</span>
            <div>
              <h1 className="text-5xl font-extrabold leading-tight" style={{ color: themeColor }}>{data.event.title}</h1>
              <div className="text-lg text-neutral-300 flex items-center gap-4 mt-2">
                {data.event.venue && <span><MapPin size={18} className="inline mr-1.5" />{data.event.venue}</span>}
                {data.event.event_time && <span><Clock size={18} className="inline mr-1.5" />{data.event.event_time}{data.event.end_time ? ` – ${data.event.end_time}` : ''}</span>}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-6xl font-extrabold tabular-nums" style={{ color: themeColor }}>{clockStr}</div>
            <div className="text-sm text-neutral-400 mt-1 uppercase tracking-widest">live</div>
          </div>
        </header>

        {/* MAIN */}
        <div className="flex-1 grid grid-cols-12 gap-6 min-h-0">
          {/* Photo wall */}
          {layout !== 'timeline' && (
            <div className={`${layout === 'photos' ? 'col-span-12' : 'col-span-7'} relative rounded-3xl overflow-hidden bg-white/[0.03] border border-white/10`}>
              {photoUrl ? (
                <div className="relative w-full h-full" ref={heroRef}>
                  {/* Click hero to spotlight tagged guests */}
                  <button
                    type="button"
                    onClick={() => heroPhoto.tagged_names?.length && triggerSpotlight(heroPhoto.tagged_names)}
                    disabled={!heroPhoto.tagged_names?.length}
                    data-testid="kiosk-hero-photo"
                    className={`absolute inset-0 w-full h-full p-0 m-0 border-0 ${heroPhoto.tagged_names?.length ? 'cursor-pointer' : 'cursor-default'}`}
                    style={{ background: 'transparent' }}
                    title={heroPhoto.tagged_names?.length ? `Click to welcome ${heroPhoto.tagged_names.join(', ')}` : 'No faces tagged in this photo'}>
                    <img key={heroPhoto.photo_id} src={photoUrl} alt=""
                      className="w-full h-full object-cover animate-kiosk-zoom"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  </button>
                  {/* Tagged-names hint chip — top-left */}
                  {heroPhoto.tagged_names?.length > 0 && (
                    <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full pointer-events-none flex items-center gap-1.5"
                      style={{ background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
                      <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: themeColor }}>👋 Tap to welcome</span>
                      <span className="text-xs text-white">{heroPhoto.tagged_names.slice(0, 2).join(', ')}{heroPhoto.tagged_names.length > 2 ? '…' : ''}</span>
                    </div>
                  )}
                  {/* Bottom gradient overlay with uploader */}
                  <div className="absolute inset-x-0 bottom-0 px-6 py-4 bg-gradient-to-t from-black/85 to-transparent pointer-events-none">
                    <div className="flex items-center justify-between">
                      <div>
                        {heroPhoto.uploader_name && (
                          <p className="text-sm text-white/90">📸 by {heroPhoto.uploader_name}</p>
                        )}
                      </div>
                      <span className="text-xs text-white/60 tabular-nums">{photoIdx + 1} / {data.photos.length}</span>
                    </div>
                  </div>
                  {/* Top corner mini grid (next 4) — each thumb is clickable too */}
                  {data.photos.length > 1 && (
                    <div className="absolute top-4 right-4 grid grid-cols-2 gap-1.5 w-32 h-32">
                      {data.photos.slice(0, 4).map((p, i) => (
                        <button key={p.photo_id}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (p.tagged_names?.length) triggerSpotlight(p.tagged_names);
                            else setPhotoIdx(i);
                          }}
                          data-testid={`kiosk-thumb-${p.photo_id}`}
                          className="p-0 m-0 border-0 bg-transparent"
                          title={p.tagged_names?.length ? `Welcome ${p.tagged_names.join(', ')}` : 'Jump to this photo'}>
                          <img src={`${API}${p.thumb}`}
                            className={`w-full h-full object-cover rounded-lg border-2 transition-all ${i === photoIdx ? 'border-white' : 'border-white/20 opacity-60 hover:opacity-100'}`} alt="" />
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Manual picker FAB — bottom-right */}
                  <button onClick={() => setShowPicker(true)}
                    data-testid="kiosk-welcome-fab"
                    className="absolute bottom-4 right-4 px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest shadow-2xl hover:scale-105 transition-transform flex items-center gap-1.5"
                    style={{ background: themeColor, color: '#fff', boxShadow: `0 8px 24px ${themeColor}80` }}>
                    👋 Welcome a guest
                  </button>
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-center px-12">
                  <div className="text-8xl mb-4">{data.event.theme_emoji}</div>
                  <p className="text-2xl text-neutral-400">Photos will appear here once guests upload</p>
                  <button onClick={() => setShowPicker(true)}
                    data-testid="kiosk-welcome-fab"
                    className="mt-6 px-5 py-3 rounded-full text-sm font-bold uppercase tracking-widest hover:scale-105 transition-transform"
                    style={{ background: themeColor, color: '#fff' }}>
                    👋 Welcome a guest
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Right column: NOW + Upcoming + Stats */}
          {layout !== 'photos' && (
            <div className={`${layout === 'timeline' ? 'col-span-12' : 'col-span-5'} flex flex-col gap-4 min-h-0`}>
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <StatBox label="Attending" value={data.stats.attending_families} icon={Users} color={themeColor} />
                <StatBox label="Kids" value={data.stats.kids} icon={Baby} color="#00E5FF" />
                <StatBox label="Total Head" value={data.stats.total_head} icon={Users} color="#A855F7" />
              </div>

              {/* NOW */}
              {data.timeline.current ? (
                <div className="rounded-3xl p-6 border-2"
                  style={{ background: `${themeColor}15`, borderColor: themeColor, boxShadow: `0 0 30px ${themeColor}50` }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: themeColor, boxShadow: `0 0 10px ${themeColor}` }} />
                    <span className="text-sm font-bold uppercase tracking-widest" style={{ color: themeColor }}>Now</span>
                    <span className="text-sm text-neutral-400 ml-auto tabular-nums">{data.timeline.current.time}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {data.timeline.current.icon && <span className="text-5xl">{data.timeline.current.icon}</span>}
                    <h2 className="text-3xl font-extrabold leading-tight">{data.timeline.current.title}</h2>
                  </div>
                  {(data.timeline.current.location || data.timeline.current.lead_owner) && (
                    <div className="text-sm text-neutral-300 mt-3 flex items-center gap-4">
                      {data.timeline.current.location && <span><MapPin size={14} className="inline mr-1" />{data.timeline.current.location}</span>}
                      {data.timeline.current.lead_owner && <span><UserIcon size={14} className="inline mr-1" />{data.timeline.current.lead_owner}</span>}
                    </div>
                  )}
                </div>
              ) : data.event.is_today ? (
                <div className="rounded-3xl p-6 bg-white/[0.04] border border-white/10">
                  <p className="text-sm text-neutral-400">Get ready — the event is about to begin!</p>
                </div>
              ) : null}

              {/* UP NEXT */}
              <div className="flex-1 rounded-3xl bg-white/[0.03] border border-white/10 p-5 overflow-hidden flex flex-col min-h-0">
                <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-3">
                  {data.timeline.current ? 'Coming Up Next' : (data.event.is_today ? 'Today\'s Schedule' : 'Schedule')}
                </h3>
                {data.timeline.upcoming.length === 0 ? (
                  <p className="text-sm text-neutral-500">No schedule items yet.</p>
                ) : (
                  <div className="space-y-3 flex-1 overflow-hidden">
                    {data.timeline.upcoming.map(it => (
                      <div key={it.item_id} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.03]">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                          style={{ background: `${themeColor}15` }}>
                          {it.icon || '⏰'}
                        </div>
                        <div className="flex-1">
                          <div className="text-base font-bold">{it.title}</div>
                          <div className="text-xs text-neutral-400 mt-0.5">
                            <span className="tabular-nums font-mono" style={{ color: themeColor }}>{it.time}</span>
                            {it.location && <> · {it.location}</>}
                            {it.lead_owner && <> · {it.lead_owner}</>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="text-center">
          <p className="text-[10px] text-neutral-600 uppercase tracking-widest">
            Live · auto-refreshing · powered by HomeCommand
          </p>
        </footer>
      </div>

      {/* ============== SPOTLIGHT OVERLAY ============== */}
      {spotlight && (
        <div key={spotlight.key} data-testid="kiosk-spotlight"
          className="absolute inset-0 z-40 flex items-center justify-center animate-spotlight-in"
          style={{
            background: `radial-gradient(ellipse at center, ${themeColor}55 0%, ${themeColor}25 40%, rgba(0,0,0,0.85) 75%)`,
            backdropFilter: 'blur(10px)',
          }}
          onClick={() => setSpotlight(null)}>
          {/* Floating sparkles */}
          {[...Array(18)].map((_, i) => (
            <span key={i} className="absolute text-2xl animate-sparkle pointer-events-none select-none"
              style={{
                left: `${(i * 53) % 100}%`,
                top: `${(i * 37) % 100}%`,
                animationDelay: `${i * 0.18}s`,
                color: themeColor,
              }}>{['✨','🎉','⭐','💫','🪩'][i % 5]}</span>
          ))}
          <div className="relative text-center px-12 max-w-5xl">
            <div className="text-9xl mb-6 animate-bounce-slow">{data.event.theme_emoji}</div>
            {spotlight.source && spotlight.source !== 'manual' && (
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
                style={{ background: 'rgba(0,0,0,0.5)', border: `1px solid ${themeColor}50` }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: themeColor }} />
                <span className="text-xs font-bold uppercase tracking-widest text-white/85">
                  {spotlight.source === 'qr_checkin' ? '✅ Just Checked In' :
                   spotlight.source === 'rsvp_submit' ? '✨ Just Joined' : 'Live'}
                </span>
              </div>
            )}
            <p className="text-3xl uppercase tracking-[0.5em] font-bold mb-4"
              style={{ color: themeColor }}>Welcome</p>
            <div className="space-y-2">
              {spotlight.names.map((n) => (
                <h2 key={n} className="text-7xl font-extrabold leading-tight text-white drop-shadow-2xl"
                  style={{ textShadow: `0 0 24px ${themeColor}, 0 0 60px ${themeColor}80` }}>{n}</h2>
              ))}
            </div>
            <p className="text-2xl text-white/85 mt-8 italic">
              {spotlight.names.length > 1 ? 'So glad you all could join us!' : 'So glad you could join us!'}
            </p>
            <p className="text-xs text-white/40 mt-8 uppercase tracking-widest">tap anywhere to dismiss</p>
          </div>
        </div>
      )}

      {/* ============== GUEST PICKER ============== */}
      {showPicker && (
        <div data-testid="kiosk-picker"
          className="absolute inset-0 z-30 flex items-center justify-center p-8"
          style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
          onClick={() => setShowPicker(false)}>
          <div className="rounded-3xl p-6 w-full max-w-xl max-h-[80vh] flex flex-col"
            style={{ background: '#0a0a0a', border: `2px solid ${themeColor}40` }}
            onClick={(e) => e.stopPropagation()}>
            <h3 className="text-2xl font-bold mb-1" style={{ color: themeColor }}>Welcome a guest</h3>
            <p className="text-sm text-neutral-400 mb-4">Pick someone to spotlight on the screen for 5 seconds.</p>
            <input value={pickerQuery} onChange={(e) => setPickerQuery(e.target.value)}
              data-testid="kiosk-picker-search"
              placeholder="Search by name..."
              autoFocus
              className="w-full px-4 py-3 rounded-xl mb-3 text-base outline-none"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff',
              }} />
            <div className="overflow-y-auto flex-1 space-y-1">
              {(data.rsvp_picker || [])
                .filter(r => !pickerQuery || r.name.toLowerCase().includes(pickerQuery.toLowerCase()))
                .slice(0, 50)
                .map(r => (
                  <button key={r.rsvp_id}
                    onClick={() => triggerSpotlight([r.name])}
                    data-testid={`kiosk-picker-${r.rsvp_id}`}
                    className="w-full text-left px-4 py-3 rounded-xl hover:scale-[1.01] transition-transform flex items-center gap-3"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold"
                      style={{ background: `${themeColor}25`, color: themeColor }}>
                      {r.name.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="text-base text-white flex-1 truncate">{r.name}</span>
                    <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: themeColor }}>Spotlight →</span>
                  </button>
                ))}
              {(data.rsvp_picker || []).filter(r => !pickerQuery || r.name.toLowerCase().includes(pickerQuery.toLowerCase())).length === 0 && (
                <p className="text-center text-neutral-500 py-6">No guests match.</p>
              )}
            </div>
            <button onClick={() => setShowPicker(false)}
              className="mt-4 px-4 py-2 rounded-full text-xs font-medium text-neutral-400 hover:text-white hover:bg-white/5 self-end">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes kiosk-zoom {
          0%   { transform: scale(1) translate(0, 0); }
          100% { transform: scale(1.08) translate(-1%, -1%); }
        }
        .animate-kiosk-zoom {
          animation: kiosk-zoom ${PHOTO_INTERVAL}ms ease-in-out forwards;
        }
        @keyframes spotlight-in {
          0%   { opacity: 0; transform: scale(0.94); }
          60%  { opacity: 1; transform: scale(1.02); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-spotlight-in {
          animation: spotlight-in 0.5s ease-out forwards;
        }
        @keyframes sparkle {
          0%, 100% { opacity: 0; transform: translate(0,0) scale(0.5) rotate(0deg); }
          50%      { opacity: 1; transform: translate(15px,-20px) scale(1.2) rotate(15deg); }
        }
        .animate-sparkle {
          animation: sparkle 2.5s ease-in-out infinite;
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-12px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 1.6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 text-center">
      <Icon size={22} className="mx-auto mb-1" style={{ color }} />
      <div className="text-3xl font-extrabold tabular-nums" style={{ color }}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-1">{label}</div>
    </div>
  );
}

function Full({ children }) {
  return <div className="min-h-screen flex items-center justify-center bg-black text-white p-8">{children}</div>;
}
